import { ExternalLink } from "lucide-react";

import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { useAmountFormatter } from "../../hooks/useAmountFormatter";
import { useCryptoSwapLabels } from "../../hooks/useCryptoSwapLabels";
import type { OtcSellOrder } from "../../types/cryptoSell.types";
import { isLiveSale } from "../../utils/cryptoSellStatus";
import { shortenAddress } from "../../utils/cryptoSwapAddress";
import { AssetAmount } from "../shared/AssetAmount";
import { CopyValueButton, DetailRow, MonoValue } from "../shared/CryptoSwapBits";
import { NetworkName } from "../shared/NetworkIcon";

function Money({ value, currency, strong }: { value: string; currency: string; strong?: boolean }) {
  return (
    <MonoValue className={strong ? "font-semibold" : undefined}>
      <AssetAmount
        value={value}
        currency={currency}
        size="sm"
        className={strong ? "font-semibold" : "font-normal"}
      />{" "}
      {currency}
    </MonoValue>
  );
}

/** Still to come while the sale can move; nothing will come once it ended without a credit. */
function NotYet({ live }: { live: boolean }) {
  const { t } = useTranslations("cryptoSwaps");
  return <span className="text-xs text-muted-foreground">{live ? t("sell_summary_pending") : "—"}</span>;
}

/** Everything the sale is, as one description list. Shared by the sale page and the history drawer. */
export function SaleDetailsList({ sale }: { sale: OtcSellOrder }) {
  const { t, activeLanguage } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const format = useAmountFormatter();
  const rule = labels.networkRule(sale.deposit.network);
  const txUrl = labels.txUrl(sale.deposit.network, sale.deposit.tx_hash);
  const refundTxUrl = labels.txUrl(sale.deposit.network, sale.refund_tx_hash);
  const live = isLiveSale(sale.status);
  const date = (value: string) => (
    <MonoValue className="text-xs">{formatDateTime(value, { locale: activeLanguage })}</MonoValue>
  );

  return (
    <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2.5">
      <DetailRow label={t("sell_summary_deposit")}>
        <Money value={sale.deposit.amount} currency={sale.deposit.currency} />
      </DetailRow>
      <DetailRow label={t("summary_price")}>
        <MonoValue>
          {t("price_line", {
            asset: sale.deposit.currency,
            price: format(sale.price.value, sale.price.currency),
            currency: sale.price.currency,
          })}
        </MonoValue>
      </DetailRow>
      <DetailRow label={t("sell_ticket_proceeds")}>
        <Money value={sale.proceeds.paid_amount ?? sale.proceeds.amount} currency={sale.proceeds.currency} />
      </DetailRow>
      <DetailRow label={t("sell_summary_fee")}>
        {sale.credit.fee_total !== null ? (
          <Money value={sale.credit.fee_total} currency={sale.credit.currency} />
        ) : (
          <NotYet live={live} />
        )}
      </DetailRow>
      <DetailRow label={t("sell_summary_credited")}>
        {sale.credit.amount !== null ? (
          <Money value={sale.credit.amount} currency={sale.credit.currency} strong />
        ) : (
          <NotYet live={live} />
        )}
      </DetailRow>
      <DetailRow label={t("summary_network")}>
        <NetworkName network={sale.deposit.network} />
      </DetailRow>
      <DetailRow label={t("sell_summary_deposit_tx")}>
        {sale.deposit.tx_hash ? (
          <span className="inline-flex flex-wrap items-center justify-end gap-1">
            <MonoValue breakAll className="text-xs">
              {shortenAddress(sale.deposit.tx_hash, 8)}
            </MonoValue>
            <CopyValueButton value={sale.deposit.tx_hash} fieldLabel={t("copy_tx_hash")} size="xs" />
            {txUrl && rule && (
              <a
                href={txUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                {t("sell_view_on_explorer", { explorer: rule.explorer })}
                <ExternalLink aria-hidden="true" className="size-3" />
              </a>
            )}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{t("sell_summary_deposit_pending")}</span>
        )}
      </DetailRow>
      <DetailRow label={t("sell_summary_payout_key")}>
        <MonoValue breakAll className="text-xs">
          {sale.payout_key}
        </MonoValue>
      </DetailRow>
      <DetailRow label={t("sell_summary_refund_wallet")}>
        <span className="inline-flex items-center justify-end gap-1">
          <MonoValue breakAll className="text-xs">
            {shortenAddress(sale.refund_address)}
          </MonoValue>
          <CopyValueButton value={sale.refund_address} fieldLabel={t("copy_address")} size="xs" />
        </span>
      </DetailRow>
      {sale.concept && <DetailRow label={t("summary_concept")}>{sale.concept}</DetailRow>}
      <DetailRow label={t("summary_created")}>{date(sale.created_at)}</DetailRow>
      {sale.completed_at && (
        <DetailRow label={t("sell_summary_completed_at")}>{date(sale.completed_at)}</DetailRow>
      )}
      {sale.expired_at && <DetailRow label={t("sell_summary_expired_at")}>{date(sale.expired_at)}</DetailRow>}
      {sale.refunded_at && (
        <DetailRow label={t("sell_summary_refunded_at")}>{date(sale.refunded_at)}</DetailRow>
      )}
      {sale.refund_tx_hash && (
        <DetailRow label={t("sell_summary_refund_tx")}>
          <span className="inline-flex items-center justify-end gap-1">
            <MonoValue className="text-xs">{shortenAddress(sale.refund_tx_hash, 8)}</MonoValue>
            <CopyValueButton value={sale.refund_tx_hash} fieldLabel={t("copy_tx_hash")} size="xs" />
            {refundTxUrl && rule && (
              <a
                href={refundTxUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("sell_refund_tx_link", { explorer: rule.explorer })}
                className="inline-flex items-center text-primary hover:underline"
              >
                <ExternalLink aria-hidden="true" className="size-3" />
              </a>
            )}
          </span>
        </DetailRow>
      )}
      <DetailRow label={t("sell_summary_sale_id")}>
        <span className="inline-flex items-center justify-end gap-1">
          <MonoValue breakAll className="text-xs text-muted-foreground">
            {sale.id}
          </MonoValue>
          <CopyValueButton value={sale.id} fieldLabel={t("copy_sale_id")} size="xs" />
        </span>
      </DetailRow>
    </dl>
  );
}

export function SaleSummaryCard({ sale }: { sale: OtcSellOrder }) {
  const { t } = useTranslations("cryptoSwaps");
  return (
    <ColocaSurface className="flex flex-col gap-4 rounded-2xl">
      <h2 className="text-xs font-semibold tracking-[0.08em] text-muted-foreground uppercase">
        {t("summary_title")}
      </h2>
      <SaleDetailsList sale={sale} />
    </ColocaSurface>
  );
}
